// components/GlobalMediaPlayer.js
import React from "react";
import Player from "./Player";
import "./globalPlayer.css";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import {
  Close,
  HeadsetRounded,
  InfoRounded,
  KeyboardArrowDownRounded,
  OpenInFull,
  TvRounded,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getEpisodes,
  getIsMaximized,
  getMedia,
  getPodcast,
  getProgressData,
  getWatchType,
  setEpisodes,
  setMaximized,
  setMedia,
  setPodcast,
  setProgress,
  setWatchType,
} from "../../../store/reducers/mediaSlice";
import ToolsLayer from "./ToolsLayer";
import {
  MediaController,
} from "media-chrome/react";
import ListEpisodesDrawer from "./ListEpisodesDrawer";
import { useIsMobile } from "../../../hooks/responsive";
import MaximizedUI from "./MaximizedUI";
import MobileMaximizedUI from "./MobileMaximizedUI";
import MinimizedOverlay from "./MinimizedOverlay";
import PlayerInfo from "./PlayerInfo";

const GlobalMediaPlayer = () => {
  const isMobile = useIsMobile();
  const isMaximized = useSelector(getIsMaximized);
  const watchType = useSelector(getWatchType);
  const media = useSelector(getMedia);
  const episodes = useSelector(getEpisodes);
  const podcast = useSelector(getPodcast);

  const [showEpisodes, setShowEpisodes] = React.useState(false);

  const dispatch = useDispatch();

  const handleMinimize = () => {
    dispatch(setMaximized(false));
  };
  const handleMaximize = () => {
    dispatch(setMaximized(true));
  };
  const handleClosePlayer = () => {
    dispatch(setPodcast(null));
    dispatch(setEpisodes(null));
    dispatch(setMedia(null));
  };

  // CSS STYLES: distinct styles for Dialog Mode vs Snackbar Mode
  const containerStyle = React.useMemo(() => {
    const commonStyle = {
      position: "fixed",
      display: "flex",
      alignItems: "flex-start",
      zIndex: (theme) => theme.zIndex.drawer - 1,
      overflow: "hidden",
      flexDirection: "column",
    };

    if (isMaximized) {
      return {
        ...commonStyle,
        bottom: 0,
        right: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.9)",
        justifyContent: "center",
        flexDirection: "row",
        flexWrap: "nowrap",
      };
    }

    const padding = isMobile ? 15 : 30;

    // minimized
    return {
      ...commonStyle,
      bottom: `${padding}px`,
      right: `${padding}px`,
      width: "500px",
      height: "auto",
      backgroundColor: "#1e1e1e",
      borderRadius: "11px",
      maxWidth: `calc(100vw - ${padding * 2}px)`,
      transition: "width 0.5s ease-in-out, height 0.5s ease-in-out",
    };
  }, [isMaximized, isMobile]);

  const trailerItem = React.useMemo(() => {
    if (!podcast?.ba) return null;
    return {
      type: "trailer",
      titre: "Bande d'annonce - " + podcast.titre,
      image: `https://medias.inrees.com/img/pro/audiovisuel/home_${podcast?.id}.jpg`,
      videoLink: podcast.ba,
    };
  }, [podcast]);

  React.useEffect(() => {
    if (!isMobile) setShowEpisodes(isMaximized);
  }, [isMobile, isMaximized]);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [volume, setVolume] = React.useState(1);

  const [wasPlayed, setWasPlayed] = React.useState(false);
  React.useEffect(() => {
    setWasPlayed(false);
  }, [media]);

  const containerRef = React.useRef(null);
  const playerRef = React.useRef(null);

  const handleSaveProgress = () => {
    const time = playerRef.current.currentTime;
    const duration = playerRef.current.duration;
    const progress = time / duration;
    dispatch(setProgress({ id: media.id, progress }));
  };

  React.useEffect(() => {
    if (playerRef.current && isPlaying) {
      const interval = setInterval(() => {
        if (playerRef.current) {
          handleSaveProgress();
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [playerRef.current, isPlaying]);

  const [duration, setDuration] = React.useState(null);
  const progressData = useSelector(getProgressData);

  React.useEffect(() => {
    if (duration && !wasPlayed && playerRef.current && media) {
      if (progressData[media.id]) {
        const video = playerRef.current;
        video.currentTime = duration * progressData[media.id];
      }
    }
  }, [progressData, media, wasPlayed, duration]);

  if (!podcast || !media || !episodes) return null;

  return (
    <Paper ref={containerRef} elevation={6} square sx={containerStyle}>
      <MediaController
        style={{
          display: "flex",
          flexDirection: "column",
          transition: "aspectRatio 0.5s ease-in-out, height 0.5s ease-in-out",
          aspectRatio: watchType === "watch" ? "16/9" : "30/9",
          width: "100%",
          height: "100%"
        }}
      >
        <Player
          ref={playerRef}
          media={media}
          isMaximized={isMaximized}
          watchType={watchType}
          playing={playerRef.current && wasPlayed === false ? true : undefined}
          onError={() => {
            setWasPlayed(false);
          }}
          onDurationChange={(data) => {
            setDuration(data.target.duration);
          }}
          onPlaying={() => {
            setIsPlaying(true);
            setWasPlayed(true);
          }}
          onPause={(data) => {
            setIsPlaying(false);
            handleSaveProgress();
          }}
          onSeeked={(data) => {
            handleSaveProgress();
          }}
          poster={media.image}
          muted={isMuted}
          setIsMuted={setIsMuted}
          volume={volume}
          setVolume={setVolume}
        />

        {isMaximized ? (
          isMobile ? (
            <MobileMaximizedUI
              watchType={watchType}
              showEpisodes={showEpisodes}
              setShowEpisodes={setShowEpisodes}
              handleMinimize={handleMinimize}
              handleClosePlayer={handleClosePlayer}
              dispatch={dispatch}
              setWatchType={setWatchType}
              setIsMuted={setIsMuted}
              setVolume={setVolume}
              playerRef={playerRef}
              isMobile={isMobile}
              isPlaying={isPlaying}
            />
          ) : (
            <MaximizedUI
              watchType={watchType}
              showEpisodes={showEpisodes}
              setShowEpisodes={setShowEpisodes}
              handleMinimize={handleMinimize}
              handleClosePlayer={handleClosePlayer}
              dispatch={dispatch}
              setWatchType={setWatchType}
              setIsMuted={setIsMuted}
              setVolume={setVolume}
              playerRef={playerRef}
              isMobile={isMobile}
            />
          )
        ) : (
          <MinimizedOverlay
            watchType={watchType}
            isMaximized={isMaximized}
            handleMaximize={handleMaximize}
            handleClosePlayer={handleClosePlayer}
            setIsMuted={setIsMuted}
          />
        )}
      </MediaController>
      {!isMaximized && (
        <PlayerInfo
          media={media}
          watchType={watchType}
          dispatch={dispatch}
          setWatchType={setWatchType}
          setShowEpisodes={setShowEpisodes}
        />
      )}
      <ListEpisodesDrawer
        isMaximized={isMaximized}
        open={showEpisodes}
        onClose={() => {
          setShowEpisodes(false);
        }}
        trailerItem={trailerItem}
        podcast={podcast}
        media={media}
        episodes={episodes}
      />
    </Paper>
  );
};

export default GlobalMediaPlayer;
